import { useEffect, useState } from "react";
import { useChat } from "../chat/use-chat";
import { useRunSessions } from "../run/use-run";
import type { RoomState } from "../session/room-controller";
import { useIdentity } from "../session/use-identity";
import { ChatPanel } from "./ChatPanel";
import { Icon } from "./Icon";
import { PeoplePanel } from "./PeoplePanel";
import { onTablistKeyDown } from "./tablist";

type Tab = "chat" | "people";

interface SidePanelProps {
  room: RoomState;
  onLeave: () => void;
  onForget: () => void;
  onClose: () => void;
  /** Sessions whose terminal can be watched (streams from their hosts). */
  watchable: string[];
  onWatch: (sessionId: string) => void;
}

/** Collaboration side panel: chat and people/invite tabs. */
export function SidePanel(props: SidePanelProps) {
  const { room, onLeave, onForget, onClose, watchable, onWatch } = props;
  const [tab, setTab] = useState<Tab>("people");
  const { state, chat } = useChat();
  const self = useIdentity();
  const sessions = useRunSessions();

  // The chat counts as read while its tab is showing.
  useEffect(() => {
    chat.setVisible(tab === "chat");
    return () => chat.setVisible(false);
  }, [chat, tab]);

  return (
    <section
      className="panel panel-side"
      aria-label="People & chat"
      data-panel="side"
      onKeyDown={(e) => {
        if (e.key === "Escape" && !e.defaultPrevented) onClose();
      }}
    >
      <header className="panel-header">
        <div
          className="tabs"
          role="tablist"
          aria-label="People & chat"
          onKeyDown={onTablistKeyDown}
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "chat"}
            tabIndex={tab === "chat" ? 0 : -1}
            onClick={() => setTab("chat")}
          >
            Chat
            {state.unread > 0 && tab !== "chat" && (
              <span className="badge" data-testid="chat-tab-unread">
                {state.unread}
              </span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "people"}
            tabIndex={tab === "people" ? 0 : -1}
            onClick={() => setTab("people")}
          >
            People{room.status === "joined" ? ` (${room.peers.length + 1})` : ""}
          </button>
        </div>
        <button
          type="button"
          className="action-button"
          aria-label="Close People & chat"
          title="Close"
          onClick={onClose}
        >
          <Icon name="close" size={15} />
        </button>
      </header>
      <div className="panel-body">
        {tab === "chat" ? (
          <ChatPanel state={state} chat={chat} selfClientId={self.clientId} />
        ) : (
          <PeoplePanel
            state={room}
            self={self}
            sessions={sessions}
            watchable={watchable}
            onWatch={onWatch}
            onLeave={onLeave}
            onForget={onForget}
          />
        )}
      </div>
    </section>
  );
}
